import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowRight, Dumbbell, Apple, TrendingUp, Star, X, Calendar, Users, Check, FileText, Video, ExternalLink, MessageCircle, Sparkles, Zap, Target, Crown, ChevronRight, ChevronLeft, Instagram, Youtube, Clock, Shield } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Componente de Carrusel de Reviews
const ReviewsCarousel = ({ reviews }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);
  
  // Auto-play del carrusel
  useEffect(() => {
    if (!isAutoPlaying || reviews.length <= 3) return;
    
    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % Math.max(1, reviews.length - 2));
    }, 5000);
    
    return () => clearInterval(interval);
  }, [isAutoPlaying, reviews.length]);
  
  const handlePrev = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  };
  
  const handleNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => Math.min(reviews.length - 3, prev + 1));
  };
  
  const visibleReviews = reviews.slice(currentIndex, currentIndex + 3);
  const showNavigation = reviews.length > 3;
  
  return (
    <div className="relative mb-12">
      {/* Navigation Buttons */}
      {showNavigation && (
        <>
          <button
            onClick={handlePrev}
            disabled={currentIndex === 0}
            className={`absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 z-10 w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center transition-all ${
              currentIndex === 0 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-pink-600 hover:border-pink-500'
            }`}
          >
            <ChevronLeft className="w-6 h-6 text-white" />
          </button>
          <button
            onClick={handleNext}
            disabled={currentIndex >= reviews.length - 3}
            className={`absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 z-10 w-12 h-12 rounded-full bg-zinc-800 border border-zinc-700 flex items-center justify-center transition-all ${
              currentIndex >= reviews.length - 3 ? 'opacity-30 cursor-not-allowed' : 'hover:bg-pink-600 hover:border-pink-500'
            }`}
          >
            <ChevronRight className="w-6 h-6 text-white" />
          </button>
        </>
      )}
      
      {/* Reviews Container */}
      <div className="overflow-hidden px-8">
        <div 
          className="flex gap-6 transition-transform duration-500 ease-in-out"
          style={{ transform: `translateX(-${currentIndex * (100 / 3 + 2)}%)` }}
        >
          {reviews.map((review, idx) => (
            <div 
              key={idx} 
              className="min-w-[calc(33.333%-16px)] bg-zinc-900/50 border border-zinc-800 rounded-2xl p-6 hover:border-pink-500/30 transition-all"
            >
              <div className="flex gap-1 mb-4">
                {[1, 2, 3, 4, 5].map((star) => (
                  <Star 
                    key={star} 
                    className={`w-5 h-5 ${star <= review.rating ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`} 
                  />
                ))}
              </div>
              <p className="text-zinc-300 mb-4 italic line-clamp-4">"{review.comment}"</p>
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-pink-500/20 rounded-full flex items-center justify-center">
                  <span className="text-pink-500 font-bold">{review.name?.charAt(0) || 'A'}</span>
                </div>
                <span className="font-medium text-white">{review.name || 'Anónimo'}</span>
              </div>
            </div>
          ))}
        </div>
      </div>
      
      {/* Dots Indicator */}
      {showNavigation && (
        <div className="flex justify-center gap-2 mt-6">
          {Array.from({ length: Math.max(1, reviews.length - 2) }).map((_, idx) => (
            <button
              key={idx}
              onClick={() => { setIsAutoPlaying(false); setCurrentIndex(idx); }}
              className={`w-2 h-2 rounded-full transition-all ${
                idx === currentIndex ? 'bg-pink-500 w-6' : 'bg-zinc-600 hover:bg-zinc-500'
              }`}
            />
          ))}
        </div>
      )}
    </div>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const [siteConfig, setSiteConfig] = useState(null);
  const [challenges, setChallenges] = useState([]);
  const [selectedChallenge, setSelectedChallenge] = useState(null);
  const [enrollForm, setEnrollForm] = useState({ name: '', email: '', phone: '' });
  const [enrolling, setEnrolling] = useState(false);
  
  // Popup de suscripción
  const [showSubscribePopup, setShowSubscribePopup] = useState(false);
  const [subscribeEmail, setSubscribeEmail] = useState('');
  const [subscribing, setSubscribing] = useState(false);
  const [subscribeSuccess, setSubscribeSuccess] = useState(false);
  
  // Stats públicas y reviews
  const [publicStats, setPublicStats] = useState({ total_users: 0, reviews: [], average_rating: 5 });
  const [reviewForm, setReviewForm] = useState({ name: '', rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [showReviewForm, setShowReviewForm] = useState(false);

  useEffect(() => {
    fetchSiteConfig();
    fetchChallenges();
    fetchPublicStats();
    
    const hasSeenPopup = localStorage.getItem('hasSeenSubscribePopup');
    const isLoggedIn = localStorage.getItem('session_token');
    
    if (!hasSeenPopup && !isLoggedIn) {
      const timer = setTimeout(() => {
        setShowSubscribePopup(true);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, []);
  
  const fetchPublicStats = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/public/stats`);
      setPublicStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };
  
  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!reviewForm.comment.trim()) {
      toast.error('Por favor escribe un comentario');
      return;
    }
    
    setSubmittingReview(true);
    try {
      await axios.post(`${BACKEND_URL}/api/public/reviews`, reviewForm);
      toast.success('¡Gracias por tu opinión!');
      setReviewForm({ name: '', rating: 5, comment: '' });
      setShowReviewForm(false);
      fetchPublicStats();
    } catch (error) {
      toast.error('Error al enviar tu opinión');
    } finally {
      setSubmittingReview(false);
    }
  };
  
  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!subscribeEmail || !subscribeEmail.includes('@')) {
      toast.error('Ingresa un email válido');
      return;
    }
    
    setSubscribing(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/subscribe`, {
        email: subscribeEmail,
        source: 'popup_landing'
      });
      
      if (response.data.already_subscribed) {
        toast.info('Ya estás suscrita');
      } else {
        setSubscribeSuccess(true);
        toast.success('¡Suscripción exitosa!');
      }
      localStorage.setItem('hasSeenSubscribePopup', 'true');
    } catch (error) {
      toast.error('Error al suscribirse');
    } finally {
      setSubscribing(false);
    }
  };
  
  const closePopup = () => {
    setShowSubscribePopup(false);
    localStorage.setItem('hasSeenSubscribePopup', 'true');
  };

  const fetchSiteConfig = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/site-config`);
      setSiteConfig(response.data);
    } catch (error) {
      console.error('Error fetching site config:', error);
    }
  };

  const fetchChallenges = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/challenges`);
      setChallenges(response.data);
    } catch (error) {
      console.error('Error fetching challenges:', error);
    }
  };

  const handleEnrollSubmit = async (e) => {
    e.preventDefault();
    setEnrolling(true);
    
    try {
      const response = await axios.post(`${BACKEND_URL}/api/challenges/enroll`, {
        challenge_id: selectedChallenge.challenge_id || selectedChallenge.content_id,
        name: enrollForm.name,
        email: enrollForm.email,
        phone: enrollForm.phone,
        origin_url: window.location.origin
      });
      
      // Si hay URL de pago, redirigir
      if (response.data.url) {
        window.location.href = response.data.url;
      } else if (response.data.free_enrollment) {
        // Inscripción gratuita exitosa
        toast.success('¡Inscripción gratuita completada!');
        setSelectedChallenge(null);
        setEnrollForm({ name: '', email: '', phone: '' });
      } else {
        toast.success('¡Inscripción exitosa!');
        setSelectedChallenge(null);
        setEnrollForm({ name: '', email: '', phone: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al inscribirse');
    } finally {
      setEnrolling(false);
    }
  };

  const getImageUrl = (imageData) => {
    if (!imageData) return null;
    if (typeof imageData === 'string') return imageData;
    if (imageData.url) return imageData.url;
    return null;
  };

  // Los programas se cargan desde el backend si están disponibles
  const [programs, setPrograms] = useState([]);
  
  useEffect(() => {
    fetchPrograms();
  }, []);
  
  const fetchPrograms = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/programs`);
      if (response.data && response.data.length > 0) {
        // Normalizar los datos (el backend puede tener 'title' o 'name')
        const normalized = response.data.map(p => {
          const id = p.id || p.level || p.program_id;
          // Determinar el período según el nivel
          let period = p.period || p.payment_type;
          if (!period || period === 'mes' || period === 'one_time') {
            // Nivel 1 y 2 son pago único, Premium es anual
            period = (id === 'premium' || p.level === 'premium') ? 'año' : 'pago único';
          }
          if (period === 'annual') period = 'año';
          
          return {
            id: id,
            level: p.level,
            program_id: p.program_id,
            name: p.name || p.title || 'Programa',
            subtitle: p.subtitle || p.description || '',
            // Public card text. Kept separate from `subtitle`, which also
            // carries the INTERNAL level label (PRINCIPIANTE/INTERMEDIO) and
            // must never render publicly.
            description: p.description || '',
            price: p.price || 0,
            period: period,
            features: p.features || [],
            popular: p.popular || (id === 'premium'),
            order: p.order || 0,
            image_url: p.image_url,
            payment_type: p.payment_type
          };
        });
        setPrograms(normalized);
      } else {
        setPrograms(defaultPrograms);
      }
    } catch (error) {
      setPrograms(defaultPrograms);
    }
  };
  
  const defaultPrograms = [
    {
      id: 'nivel_1',
      name: 'NIVEL 1',
      subtitle: 'PRINCIPIANTE',
      price: 39,
      period: 'pago único',
      features: ['Videos de entrenamiento', 'Plan de nutrición básico', 'Comunidad privada', 'Soporte por email'],
      popular: false,
      order: 1
    },
    {
      id: 'nivel_2', 
      name: 'NIVEL 2',
      subtitle: 'INTERMEDIO',
      price: 59,
      period: 'pago único',
      features: ['Todo del Nivel 1', 'Rutinas avanzadas', 'Plan nutricional personalizado', 'Sesión de coaching mensual', 'Acceso a challenges'],
      popular: false,
      order: 2
    },
    {
      id: 'premium',
      name: 'PREMIUM',
      subtitle: 'TRANSFORMACIÓN TOTAL',
      price: 199,
      period: 'año',
      features: ['Acceso completo ilimitado', 'Coaching 1:1 semanal', 'Plan 100% personalizado', 'Prioridad en respuestas', 'Contenido exclusivo', 'Todos los ebooks gratis'],
      popular: true,
      order: 0
    }
  ];

  // Función para obtener imagen de programa desde siteConfig
  const getProgramImage = (programId) => {
    const defaults = {
      'nivel_1': 'https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400',
      'nivel_2': 'https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?w=400',
      'premium': 'https://images.unsplash.com/photo-1550345332-09e3ac987658?w=400'
    };
    
    if (siteConfig?.programs_images) {
      // Buscar por el nivel exacto
      const configImage = siteConfig.programs_images[programId];
      if (configImage) return configImage;
      
      // Fallback: premium puede usar nivel_3
      if (programId === 'premium' && siteConfig.programs_images.nivel_3) {
        return siteConfig.programs_images.nivel_3;
      }
    }
    
    return defaults[programId] || defaults['premium'];
  };
  
  // El Club VIP ya tiene su tarjeta fija mas arriba (Hero Split Section), asi
  // que de la cuadricula solo se excluye ESE programa puntual por su ID unico.
  // Antes se excluia por level==='premium' && payment_type==='annual', lo cual
  // ocultaba cualquier otro curso premium legitimamente anual (ej. el curso de
  // esta clienta), no solo el Club VIP.
  const CLUB_VIP_PROGRAM_ID = 'prog_nivel_3_001';
  const isVipSubscription = (p) => p.program_id === CLUB_VIP_PROGRAM_ID;

  // Ordenar programas (popular primero, luego por order)
  const sortedPrograms = [...(programs.length > 0 ? programs : defaultPrograms)].sort((a, b) => {
    if (a.popular && !b.popular) return -1;
    if (!a.popular && b.popular) return 1;
    return (a.order || 0) - (b.order || 0);
  });

  return (
    <div className="min-h-screen bg-[#09090b] text-white overflow-x-hidden">
      {/* Grain Texture Overlay */}
      <div className="grain" />
      
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#09090b] border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-6 py-3 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <span className="font-barlow text-xl font-bold text-white tracking-tight">
              Booty<span className="text-pink-500">&</span>Legs Academy
            </span>
          </Link>
          
          <div className="hidden md:flex items-center gap-1">
            <Link to="/academia" className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 font-medium text-sm transition-colors">
              Academia
            </Link>
            <a href="#programas" className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 font-medium text-sm transition-colors">
              Club VIP
            </a>
            <Link to="/coaching" className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 font-medium text-sm transition-colors">
              Coaching 1-1
            </Link>
            <Link to="/mindset" className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 font-medium text-sm transition-colors">
              Mindset
            </Link>
            <Link to="/ebooks" className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 font-medium text-sm transition-colors">
              E-Books
            </Link>
          </div>
          
          <div className="hidden md:flex items-center gap-3">
            <Link to="/login" className="px-4 py-2 rounded-lg text-zinc-400 hover:text-white hover:bg-zinc-800 font-medium text-sm transition-colors">
              Iniciar Sesión
            </Link>
            <Link to="/register">
              <Button className="bg-pink-600 hover:bg-pink-500 text-white font-bold px-5 py-2 rounded-full transition-all hover:scale-105 text-sm">
                Comenzar
              </Button>
            </Link>
          </div>
          
          <Link to="/login" className="md:hidden">
            <Button size="sm" className="bg-pink-600 hover:bg-pink-500 rounded-full">
              Entrar
            </Button>
          </Link>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center pt-20">
        {/* Background Image */}
        <div className="absolute inset-0 z-0 bg-[#09090b]">
          {siteConfig && (
            <img
              src={siteConfig.landing_hero_image || "https://images.unsplash.com/photo-1571019614242-c5c5dee9f50b?q=80&w=2070"}
              alt="Fitness"
              className="w-full h-full object-cover animate-fadeIn"
              style={{ objectPosition: 'center top' }}
              loading="eager"
            />
          )}
          {/* Gradiente suave solo en la parte inferior y lado izquierdo para legibilidad */}
          <div className="absolute inset-0 bg-gradient-to-t from-[#09090b] via-transparent to-transparent opacity-90" />
          <div className="absolute inset-0 bg-gradient-to-r from-[#09090b]/95 via-[#09090b]/50 to-transparent" />
        </div>
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-20 md:py-32 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 items-center">
            {/* Texto a la izquierda */}
            <div className="max-w-xl">
              <div className="inline-flex items-center gap-2 bg-pink-500/20 backdrop-blur-sm border border-pink-500/30 rounded-full px-4 py-2 mb-8">
                <Sparkles className="w-4 h-4 text-pink-500" />
                <span className="text-pink-400 text-sm font-medium uppercase tracking-wider">
                  {siteConfig?.hero_subtitle || 'Academia de Transformación'}
                </span>
              </div>
              
              <h1 className="font-barlow text-4xl md:text-6xl lg:text-7xl font-black uppercase leading-none mb-6">
                <span className="text-gradient">TRANSFORMA</span>
                <br />
                <span className="text-white drop-shadow-lg">TU CUERPO</span>
                <br />
                <span className="text-zinc-400">TU VIDA</span>
              </h1>
              
              <p className="text-base md:text-lg text-zinc-300 max-w-md mb-8 leading-relaxed drop-shadow-md">
                {siteConfig?.hero_description || (
                  <>Programas de entrenamiento especializados en <span className="text-pink-400 font-semibold">glúteos y piernas</span> diseñados por entrenadoras certificadas.</>
                )}
              </p>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button className="bg-pink-600 hover:bg-pink-500 text-white font-bold uppercase tracking-wider px-8 py-6 rounded-full transition-all hover:scale-105 glow-pink text-lg group">
                    Comenzar Ahora
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <a href="#programas">
                  <Button variant="outline" className="border-white/30 hover:border-pink-500 text-white hover:text-pink-400 font-bold uppercase tracking-wider px-8 py-6 rounded-full transition-all bg-black/30 backdrop-blur-sm">
                    Ver Programas
                  </Button>
                </a>
              </div>
              
              {/* Stats */}
              <div className="flex gap-8 md:gap-12 mt-12 pt-6 border-t border-white/20">
                <div>
                  <div className="font-barlow text-3xl md:text-4xl font-black text-white">{publicStats.total_reviews || 0}</div>
                  <div className="text-zinc-400 text-sm uppercase tracking-wider mt-1">Reseñas</div>
                </div>
                <div>
                  <div className="font-barlow text-3xl md:text-4xl font-black text-pink-500">{publicStats.average_rating || 5.0}</div>
                  <div className="text-zinc-400 text-sm uppercase tracking-wider mt-1">Rating</div>
                </div>
              </div>
            </div>
            
            {/* Espacio derecho para que se vea la foto */}
            <div className="hidden lg:block"></div>
          </div>
        </div>
      </section>

      {/* Features Bento Grid */}
      <section className="py-24 md:py-32 relative">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-pink-500 text-sm font-bold uppercase tracking-[0.2em]">¿Por qué elegirnos?</span>
            <h2 className="font-barlow text-4xl md:text-6xl font-bold uppercase tracking-tight mt-4">
              TODO LO QUE <span className="text-pink-500">NECESITAS</span>
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-6">
            {/* Main Feature Card */}
            <div className="md:col-span-2 md:row-span-2 group relative overflow-hidden rounded-3xl bg-zinc-900/50 border border-white/5 p-8 md:p-12 hover:border-pink-500/30 transition-all duration-500">
              <div className="absolute inset-0 bg-gradient-to-br from-pink-500/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-8">
                  <div className="w-16 h-16 rounded-2xl bg-pink-500/20 flex items-center justify-center">
                    <Target className="w-8 h-8 text-pink-500" />
                  </div>
                  <span className="text-pink-500 text-sm font-bold uppercase tracking-[0.2em]">Especialización</span>
                </div>
                <h3 className="font-barlow text-3xl md:text-4xl font-bold uppercase mb-4">
                  GLÚTEOS & PIERNAS
                </h3>
                <p className="text-zinc-400 text-lg leading-relaxed max-w-md mb-8">
                  Nos especializamos en programas creados para mujeres que desean transformar su cuerpo de forma inteligente, con énfasis en piernas y glúteos, logrando una recomposición corporal real y duradera.
                </p>
                <div className="flex gap-4">
                  <div className="flex -space-x-3">
                    <img src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=100" className="w-10 h-10 rounded-full border-2 border-zinc-900 object-cover" alt="" />
                    <img src="https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=100" className="w-10 h-10 rounded-full border-2 border-zinc-900 object-cover" alt="" />
                    <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=100" className="w-10 h-10 rounded-full border-2 border-zinc-900 object-cover" alt="" />
                  </div>
                  <span className="text-zinc-500 text-sm self-center">+500 transformaciones</span>
                </div>
              </div>
            </div>
            
            {/* Small Cards */}
            <div className="group relative overflow-hidden rounded-3xl bg-zinc-900/50 border border-white/5 p-6 hover:border-pink-500/30 transition-all duration-500">
              <Dumbbell className="w-10 h-10 text-pink-500 mb-4" />
              <h3 className="font-barlow text-xl font-bold uppercase mb-2">Entrenamiento</h3>
              <p className="text-zinc-500 text-sm">Videos HD paso a paso con técnica perfecta</p>
            </div>
            
            <div className="group relative overflow-hidden rounded-3xl bg-zinc-900/50 border border-white/5 p-6 hover:border-pink-500/30 transition-all duration-500">
              <Apple className="w-10 h-10 text-pink-500 mb-4" />
              <h3 className="font-barlow text-xl font-bold uppercase mb-2">Nutrición</h3>
              <p className="text-zinc-500 text-sm">Planes alimenticios personalizados</p>
            </div>
            
            <div className="group relative overflow-hidden rounded-3xl bg-zinc-900/50 border border-white/5 p-6 hover:border-pink-500/30 transition-all duration-500">
              <Users className="w-10 h-10 text-pink-500 mb-4" />
              <h3 className="font-barlow text-xl font-bold uppercase mb-2">Comunidad</h3>
              <p className="text-zinc-500 text-sm">Red de apoyo exclusiva de alumnas</p>
            </div>
            
            <div className="md:col-span-2 group relative overflow-hidden rounded-3xl bg-gradient-to-r from-pink-600 to-pink-500 p-6 md:p-8 hover:scale-[1.02] transition-all duration-500">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-barlow text-2xl md:text-3xl font-bold uppercase mb-2">¿LISTA PARA EMPEZAR?</h3>
                  <p className="text-pink-100">Tu transformación comienza con un click</p>
                </div>
                <Link to="/register">
                  <Button className="bg-white text-pink-600 hover:bg-zinc-100 font-bold uppercase px-6 py-3 rounded-full">
                    Unirme <ChevronRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Club VIP Section - Hero Split Premium */}
      <section id="programas" className="py-24 md:py-32 relative overflow-hidden">
        {/* Glow backgrounds */}
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-500/5 to-transparent pointer-events-none" />
        <div className="absolute top-1/2 -left-32 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute bottom-0 -right-32 w-96 h-96 bg-pink-500/10 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto px-6 relative">
          {/* Header */}
          <div className="text-center mb-14">
            <div className="inline-flex items-center gap-2 bg-pink-500/10 border border-pink-500/30 rounded-full px-5 py-2 mb-6" data-testid="club-vip-badge">
              <Crown className="w-4 h-4 text-pink-400" />
              <span className="text-pink-400 text-xs font-bold uppercase tracking-[0.25em]">Membresía exclusiva</span>
            </div>
            <h2 className="font-barlow text-4xl md:text-6xl lg:text-7xl font-black uppercase tracking-tight">
              CLUB <span className="text-pink-500">VIP</span>
            </h2>
          </div>

          {/* Hero Split Card */}
          <div 
            className="grid grid-cols-1 lg:grid-cols-2 gap-0 rounded-[2rem] overflow-hidden border-2 border-pink-500/40 bg-gradient-to-br from-zinc-900 via-zinc-900 to-pink-950/30 shadow-[0_0_80px_rgba(236,72,153,0.15)]"
            data-testid="club-vip-hero-card"
          >
            {/* LEFT - Image */}
            <div className="relative min-h-[420px] lg:min-h-[640px] overflow-hidden">
              <img 
                src={getProgramImage('premium')}
                alt="Club VIP"
                className="absolute inset-0 w-full h-full object-cover"
              />
              {/* Mobile gradient (bottom) / Desktop gradient (right) */}
              <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 via-zinc-900/30 to-transparent lg:bg-gradient-to-r lg:from-transparent lg:via-transparent lg:to-zinc-900" />
            </div>

            {/* RIGHT - Content */}
            <div className="p-8 md:p-12 flex flex-col justify-center">
              <h3 className="font-barlow text-4xl md:text-5xl lg:text-6xl font-black text-white uppercase mb-6 leading-[0.95]">
                PAGO <span className="text-pink-500">ANUAL</span>
              </h3>

              {/* Features grid */}
              <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-8">
                {[
                  'TODOS los niveles de entrenamiento',
                  '6 días de entrenamiento en video',
                  'Plan de alimentación 100% personalizado, creado según tu perfil y objetivos individuales, actualizado cuando lo necesites',
                  'Registro y seguimiento completo',
                  'Biblioteca educativa completa',
                  'TODOS los Ebooks incluidos',
                  'Conversatorios exclusivos',
                  'Acceso GRATIS a todos los Challenges',
                  'Recetas exclusivas',
                ].map((f, i) => (
                  <li key={i} className="flex items-start gap-2.5 text-zinc-200 text-sm">
                    <span className="w-5 h-5 rounded-full bg-pink-500/20 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <Check className="w-3 h-3 text-pink-400" />
                    </span>
                    <span>{f}</span>
                  </li>
                ))}
              </ul>

              {/* Price + CTA */}
              <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between gap-5 pt-6 border-t border-zinc-800">
                <div>
                  <div className="flex items-baseline gap-2">
                    <span className="font-barlow text-5xl md:text-6xl font-black text-white">$99</span>
                    <span className="text-zinc-500 text-base">/año</span>
                  </div>
                  <p className="text-xs text-zinc-500 mt-1">Pago único anual · Sin letra chica</p>
                </div>
                <Link to="/checkout?plan=premium" className="w-full sm:w-auto" data-testid="club-vip-cta-landing">
                  <Button className="w-full sm:w-auto bg-pink-600 hover:bg-pink-500 text-white font-bold uppercase tracking-wider px-8 py-6 rounded-full glow-pink text-base group">
                    Quiero entrar
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
              </div>
            </div>
          </div>

          {/* Trust Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-8">
            <div className="flex items-center gap-3 bg-zinc-900/50 border border-zinc-800 rounded-2xl px-5 py-4">
              <Shield className="w-5 h-5 text-pink-500 flex-shrink-0" />
              <span className="text-sm text-zinc-300">Pago 100% seguro</span>
            </div>
            <div className="flex items-center gap-3 bg-zinc-900/50 border border-zinc-800 rounded-2xl px-5 py-4">
              <Zap className="w-5 h-5 text-pink-500 flex-shrink-0" />
              <span className="text-sm text-zinc-300">Acceso inmediato</span>
            </div>
            <div className="flex items-center gap-3 bg-zinc-900/50 border border-zinc-800 rounded-2xl px-5 py-4">
              <Crown className="w-5 h-5 text-pink-500 flex-shrink-0" />
              <span className="text-sm text-zinc-300">Comunidad exclusiva</span>
            </div>
          </div>

          {/* Programas individuales */}
          {sortedPrograms.filter((p) => !isVipSubscription(p)).length > 0 && (
            <div className="mt-20">
              <div className="text-center mb-10">
                <span className="text-pink-500 text-sm font-bold uppercase tracking-[0.2em]">Elige tu nivel</span>
                <h3 className="font-barlow text-3xl md:text-5xl font-bold uppercase tracking-tight mt-4">
                  NUESTROS <span className="text-pink-500">PROGRAMAS</span>
                </h3>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {sortedPrograms.filter((p) => !isVipSubscription(p)).map((program) => {
                  const programKey = program.program_id || program.id;
                  return (
                    <div
                      key={programKey}
                      className="group relative overflow-hidden rounded-3xl bg-zinc-900/50 border border-white/5 hover:border-pink-500/30 transition-all duration-500"
                      data-testid={`program-card-${programKey}`}
                    >
                      <div className="relative h-48 overflow-hidden">
                        <img
                          src={program.image_url || getProgramImage(program.level)}
                          alt={program.name || program.title}
                          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                      </div>
                      <div className="p-6">
                        <h3 className="font-barlow text-2xl font-bold uppercase mt-1 mb-2">{program.name || program.title}</h3>
                        {program.description && (
                          <p className="text-zinc-400 text-sm mb-4 whitespace-pre-line">{program.description}</p>
                        )}
                        <div className="flex items-end justify-between">
                          <div>
                            {program.original_price && program.original_price > program.price && (
                              <span className="text-zinc-500 text-sm line-through mr-2">${program.original_price}</span>
                            )}
                            <div className="font-barlow text-3xl font-black text-pink-500">${program.price}</div>
                          </div>
                          <Link to={`/checkout?plan=${program.program_id || program.id || program.level || programKey}`} data-testid={`program-cta-${programKey}`}>
                            <Button className="bg-pink-600 hover:bg-pink-500 text-white font-bold uppercase text-sm px-6 py-3 rounded-full">
                              Elegir
                            </Button>
                          </Link>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Challenges Section */}
      <section id="challenges" className="py-24 md:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-pink-500/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="text-center mb-16">
            <span className="text-pink-500 text-sm font-bold uppercase tracking-[0.2em]">Retos</span>
            <h2 className="font-barlow text-4xl md:text-6xl font-bold uppercase tracking-tight mt-4">
              CHALLENGES <span className="text-pink-500">EXCLUSIVOS</span>
            </h2>
            <p className="text-zinc-400 mt-4 max-w-2xl mx-auto">
              Únete a nuestros retos mensuales y transforma tu cuerpo junto a nuestra comunidad
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {challenges.length > 0 ? (
              challenges.map((challenge, index) => {
                const isComingSoon = challenge.status === 'coming_soon';
                const isFirst = index === 0 && !isComingSoon;
                
                return (
                  <div 
                    key={challenge.challenge_id}
                    className={`group relative overflow-hidden rounded-3xl transition-all duration-500 ${
                      isComingSoon
                        ? 'bg-zinc-900/30 border border-yellow-500/30 opacity-80'
                        : isFirst 
                          ? 'bg-gradient-to-br from-pink-600/20 to-zinc-900 border-2 border-pink-500' 
                          : 'bg-zinc-900/50 border border-white/5 hover:border-pink-500/30'
                    }`}
                    data-testid={`challenge-${challenge.challenge_id}`}
                  >
                    {/* Badge Próximamente */}
                    {isComingSoon && (
                      <div className="absolute top-4 left-4 z-10 bg-yellow-500 text-black text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        Próximamente
                      </div>
                    )}
                    
                    {/* Badge Destacado */}
                    {isFirst && !isComingSoon && (
                      <div className="absolute top-4 right-4 z-10 bg-pink-500 text-white text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full">
                        Destacado
                      </div>
                    )}
                    
                    {/* Imagen */}
                    {(challenge.cover_image || challenge.image_url) && (
                      <div className="relative h-48 overflow-hidden">
                        <img 
                          src={getImageUrl(challenge.cover_image || challenge.image_url)}
                          alt={challenge.title}
                          className={`w-full h-full object-cover transition-transform duration-700 ${
                            isComingSoon ? 'grayscale-[30%]' : 'group-hover:scale-110'
                          }`}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-zinc-900 to-transparent" />
                        
                        {/* Overlay de bloqueo para próximamente */}
                        {isComingSoon && (
                          <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                            <div className="bg-yellow-500/90 text-black px-4 py-2 rounded-full font-bold text-sm flex items-center gap-2">
                              <Clock className="w-4 h-4" />
                              Muy Pronto
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    
                    <div className="p-6">
                      <div className="flex items-center gap-2 mb-3">
                        <Calendar className="w-4 h-4 text-pink-500" />
                        <span className="text-zinc-500 text-sm">
                          {challenge.start_date 
                            ? new Date(challenge.start_date).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
                            : 'Próximamente'
                          }
                        </span>
                      </div>
                      
                      <h3 className="font-barlow text-2xl font-bold uppercase mb-4">{challenge.title}</h3>
                      
                      <div className="flex items-end justify-between">
                        <div>
                          <span className="text-zinc-500 text-sm">Desde</span>
                          <div className="font-barlow text-3xl font-black text-pink-500">${challenge.price || 29}</div>
                        </div>
                        
                        {isComingSoon ? (
                          <Button 
                            disabled
                            className="bg-zinc-700 text-zinc-400 font-bold uppercase text-sm px-6 py-3 rounded-full cursor-not-allowed"
                          >
                            <Clock className="w-4 h-4 mr-2" />
                            Próximamente
                          </Button>
                        ) : (
                          <Button 
                            onClick={() => navigate(`/checkout?challenge=${challenge.challenge_id || challenge.content_id}`)}
                            className={`${
                              isFirst 
                                ? 'bg-pink-600 hover:bg-pink-500 glow-pink' 
                                : 'bg-zinc-800 hover:bg-zinc-700'
                            } text-white font-bold uppercase text-sm px-6 py-3 rounded-full transition-all`}
                          >
                            Inscribirme
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              // Fallback cards
              [1, 2, 3].map((i) => (
                <div key={i} className="group relative overflow-hidden rounded-3xl bg-zinc-900/50 border border-white/5 p-6 hover:border-pink-500/30 transition-all">
                  <div className="h-32 bg-zinc-800/50 rounded-2xl mb-4 animate-pulse" />
                  <div className="h-6 w-3/4 bg-zinc-800/50 rounded mb-3 animate-pulse" />
                  <div className="h-4 w-1/2 bg-zinc-800/50 rounded animate-pulse" />
                </div>
              ))
            )}
          </div>
        </div>
      </section>


      {/* Trainers Section */}
      <section className="py-24 md:py-32 relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-pink-500/10 to-transparent" />
        <div className="max-w-7xl mx-auto px-6 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-pink-500 text-sm font-bold uppercase tracking-[0.2em]">Tus Entrenadoras</span>
              <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight mt-4 mb-6">
                KAREN <span className="text-pink-500">&</span> DEISY
              </h2>
              <p className="text-zinc-400 text-lg leading-relaxed mb-8">
                Entrenadoras certificadas en transformación corporal. Especializadas en entrenamiento de glúteos y piernas, nutrición deportiva y mindset fitness.
              </p>
              <div className="flex gap-4">
                <a href="#" className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-pink-500 transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="#" className="w-12 h-12 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-pink-500 transition-colors">
                  <Youtube className="w-5 h-5" />
                </a>
              </div>
            </div>
            
            <div className="relative">
              <div className="grid grid-cols-2 gap-4">
                {/* Deisy */}
                <div className="relative">
                  <img 
                    src={siteConfig?.about_image_deisy || siteConfig?.about_image_1 || "https://images.unsplash.com/photo-1609899464926-209bc7c36937?w=400"} 
                    alt="Deisy Fit" 
                    className="rounded-3xl h-64 w-full object-cover"
                  />
                  <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-lg">
                    <span className="font-bold">Deisy Fit</span>
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-pink-500 text-white px-3 py-2 rounded-xl font-bold text-center">
                    <span className="font-barlow text-xl">7+</span>
                    <span className="text-xs block">años</span>
                  </div>
                </div>
                {/* Karen */}
                <div className="relative mt-8">
                  <img 
                    src={siteConfig?.about_image_karen || siteConfig?.about_image_2 || "https://images.unsplash.com/photo-1518611012118-696072aa579a?w=400"} 
                    alt="Karen" 
                    className="rounded-3xl h-64 w-full object-cover"
                  />
                  <div className="absolute bottom-3 left-3 bg-black/70 backdrop-blur-sm text-white px-3 py-1 rounded-lg">
                    <span className="font-bold">Karen</span>
                  </div>
                  <div className="absolute -bottom-2 -right-2 bg-pink-500 text-white px-3 py-2 rounded-xl font-bold text-center">
                    <span className="font-barlow text-xl">6+</span>
                    <span className="text-xs block">años</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 md:py-32 relative">
        <div className="absolute inset-0 bg-gradient-to-t from-pink-500/20 to-transparent" />
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-barlow text-4xl md:text-6xl font-black uppercase mb-6">
            ¿LISTA PARA <span className="text-pink-500">TRANSFORMARTE</span>?
          </h2>
          <p className="text-zinc-400 text-lg mb-10 max-w-2xl mx-auto">
            Únete a miles de mujeres que ya están transformando su cuerpo y su vida con Booty & Legs Academy.
          </p>
          <Link to="/register">
            <Button className="bg-pink-600 hover:bg-pink-500 text-white font-bold uppercase tracking-wider px-12 py-6 rounded-full transition-all hover:scale-105 glow-pink-intense text-lg">
              Comenzar Mi Transformación
              <Zap className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Reviews Section */}
      <section className="py-24 relative border-t border-zinc-800">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="text-pink-500 text-sm font-bold uppercase tracking-[0.2em]">Testimonios</span>
            <h2 className="font-barlow text-4xl md:text-5xl font-bold uppercase tracking-tight mt-4">
              LO QUE DICEN <span className="text-pink-500">NUESTRAS ALUMNAS</span>
            </h2>
          </div>
          
          {/* Average Rating Display */}
          <div className="flex justify-center items-center gap-4 mb-12">
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map((star) => (
                <Star 
                  key={star} 
                  className={`w-8 h-8 ${star <= Math.round(publicStats.average_rating) ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600'}`} 
                />
              ))}
            </div>
            <span className="text-3xl font-bold text-white">{publicStats.average_rating || 5.0}</span>
            <span className="text-zinc-500">({publicStats.total_reviews || 0} reseñas)</span>
          </div>

          {/* Reviews Carousel */}
          {publicStats.reviews && publicStats.reviews.length > 0 ? (
            <ReviewsCarousel reviews={publicStats.reviews} />
          ) : (
            <div className="text-center py-8 text-zinc-500 mb-12">
              <p>Aún no hay reseñas. ¡Sé la primera en dejar tu opinión!</p>
            </div>
          )}

          {/* Review Form Toggle */}
          <div className="text-center">
            {!showReviewForm ? (
              <Button 
                onClick={() => setShowReviewForm(true)}
                className="bg-pink-600 hover:bg-pink-500 text-white rounded-full px-8 py-6 font-bold text-lg"
              >
                <Star className="w-5 h-5 mr-2" />
                Deja tu opinión
              </Button>
            ) : (
              <div className="max-w-lg mx-auto bg-zinc-900/50 border border-zinc-800 rounded-3xl p-8">
                <h3 className="font-barlow text-2xl font-bold text-white mb-6">¿Cómo fue tu experiencia?</h3>
                <form onSubmit={handleSubmitReview} className="space-y-6">
                  {/* Rating Stars */}
                  <div className="text-center">
                    <p className="text-zinc-400 mb-3">Tu calificación</p>
                    <div className="flex justify-center gap-2">
                      {[1, 2, 3, 4, 5].map((star) => (
                        <button
                          key={star}
                          type="button"
                          onClick={() => setReviewForm({...reviewForm, rating: star})}
                          className="transition-transform hover:scale-110"
                        >
                          <Star 
                            className={`w-10 h-10 cursor-pointer ${star <= reviewForm.rating ? 'text-yellow-400 fill-yellow-400' : 'text-zinc-600 hover:text-yellow-400'}`} 
                          />
                        </button>
                      ))}
                    </div>
                  </div>
                  
                  {/* Name */}
                  <div>
                    <Input
                      value={reviewForm.name}
                      onChange={(e) => setReviewForm({...reviewForm, name: e.target.value})}
                      placeholder="Tu nombre (opcional)"
                      className="bg-zinc-800 border-zinc-700 text-white placeholder:text-zinc-500 focus:border-pink-500 rounded-xl"
                    />
                  </div>
                  
                  {/* Comment */}
                  <div>
                    <textarea
                      value={reviewForm.comment}
                      onChange={(e) => setReviewForm({...reviewForm, comment: e.target.value})}
                      placeholder="Cuéntanos tu experiencia con Booty & Legs..."
                      rows={4}
                      className="w-full px-4 py-3 bg-zinc-800 border border-zinc-700 text-white placeholder:text-zinc-500 focus:border-pink-500 rounded-xl resize-none"
                      required
                    />
                  </div>
                  
                  {/* Buttons */}
                  <div className="flex gap-3">
                    <Button 
                      type="submit"
                      disabled={submittingReview}
                      className="flex-1 bg-pink-600 hover:bg-pink-500 text-white rounded-full py-6 font-bold"
                    >
                      {submittingReview ? 'Enviando...' : 'Enviar mi opinión'}
                    </Button>
                    <Button 
                      type="button"
                      onClick={() => setShowReviewForm(false)}
                      variant="outline"
                      className="border-zinc-700 text-zinc-400 hover:text-white rounded-full py-6"
                    >
                      Cancelar
                    </Button>
                  </div>
                </form>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Subscribe Popup */}
      {showSubscribePopup && !subscribeSuccess && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={closePopup}
          data-testid="subscribe-popup-overlay"
        >
          <div 
            className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-md p-8 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-pink-500/20 rounded-full blur-3xl pointer-events-none" />
            
            <button 
              onClick={closePopup}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-zinc-700 hover:bg-red-500 transition-colors"
              data-testid="close-subscribe-popup"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="relative z-10">
              <div className="w-16 h-16 rounded-2xl bg-pink-500/20 flex items-center justify-center mb-6">
                <Sparkles className="w-8 h-8 text-pink-500" />
              </div>
              
              <h3 className="font-barlow text-2xl font-bold uppercase mb-2">ÚNETE A LA COMUNIDAD</h3>
              <p className="text-zinc-500 mb-4">Recibe tips exclusivos, descuentos y contenido gratuito</p>
              
              {/* Código de descuento de bienvenida */}
              <div className="bg-gradient-to-r from-pink-600/20 to-pink-500/10 border border-pink-500/30 rounded-xl p-4 mb-6">
                <div className="flex items-center gap-2 mb-1">
                  <Crown className="w-4 h-4 text-pink-400" />
                  <span className="text-pink-400 text-xs font-bold uppercase tracking-wider">Regalo de bienvenida</span>
                </div>
                <p className="text-white font-bold text-lg">10% OFF en tu primera compra</p>
                <div className="flex items-center gap-2 mt-2">
                  <code className="bg-zinc-950 text-pink-400 px-3 py-1 rounded-lg font-mono text-sm font-bold">BIENVENIDA10</code>
                  <span className="text-zinc-500 text-xs">Copia y usa este código</span>
                </div>
              </div>
              
              <form onSubmit={handleSubscribe} className="space-y-4">
                <Input
                  type="email"
                  placeholder="tu@email.com"
                  value={subscribeEmail}
                  onChange={(e) => setSubscribeEmail(e.target.value)}
                  required
                  className="bg-zinc-950 border-zinc-800 focus:border-pink-500 rounded-xl h-12"
                />
                <Button 
                  type="submit"
                  disabled={subscribing}
                  className="w-full bg-pink-600 hover:bg-pink-500 text-white font-bold uppercase py-4 rounded-full glow-pink"
                >
                  {subscribing ? 'Suscribiendo...' : 'Suscribirme Gratis'}
                </Button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Success Popup - Después de suscribirse */}
      {showSubscribePopup && subscribeSuccess && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          onClick={closePopup}
          data-testid="subscribe-success-overlay"
        >
          <div 
            className="bg-zinc-900 border border-zinc-800 rounded-3xl w-full max-w-md p-8 relative overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="absolute top-0 right-0 w-32 h-32 bg-green-500/20 rounded-full blur-3xl pointer-events-none" />
            <div className="absolute bottom-0 left-0 w-24 h-24 bg-pink-500/20 rounded-full blur-3xl pointer-events-none" />
            
            <button 
              onClick={closePopup}
              className="absolute top-4 right-4 z-20 w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center hover:bg-zinc-700 hover:bg-red-500 transition-colors"
              data-testid="close-success-popup"
            >
              <X className="w-5 h-5" />
            </button>
            
            <div className="relative z-10 text-center">
              {/* Icono de éxito */}
              <div className="w-20 h-20 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6 animate-fadeIn">
                <Check className="w-10 h-10 text-green-500" />
              </div>
              
              <h3 className="font-barlow text-3xl font-bold uppercase mb-2 text-white">¡BIENVENIDA!</h3>
              <p className="text-zinc-400 mb-6">Ya eres parte de nuestra comunidad fitness</p>
              
              {/* Código de descuento destacado */}
              <div className="bg-gradient-to-r from-pink-600/30 to-pink-500/20 border-2 border-pink-500/50 rounded-2xl p-5 mb-6">
                <div className="flex items-center justify-center gap-2 mb-2">
                  <Crown className="w-5 h-5 text-pink-400" />
                  <span className="text-pink-400 text-sm font-bold uppercase tracking-wider">Tu código exclusivo</span>
                </div>
                <code className="block bg-zinc-950 text-pink-400 px-6 py-3 rounded-xl font-mono text-2xl font-bold mb-2">BIENVENIDA10</code>
                <p className="text-white font-bold text-lg mb-1">10% de descuento en tu primera compra</p>
                <p className="text-zinc-500 text-xs">⏰ Este código expira en 7 días</p>
              </div>
              
              {/* CTA para crear cuenta */}
              <div className="space-y-3">
                <p className="text-zinc-300 text-sm mb-4">Únete al Club VIP y desbloquea todo</p>
                
                {/* Programa Club VIP destacado */}
                <Link to="/checkout?plan=premium" onClick={closePopup}>
                  <Button className="w-full bg-gradient-to-r from-pink-600 to-pink-500 hover:from-pink-500 hover:to-pink-400 text-white font-bold uppercase py-5 rounded-full glow-pink-intense text-lg group">
                    <Crown className="w-5 h-5 mr-2" />
                    Quiero entrar al Club VIP
                    <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </Button>
                </Link>
                <p className="text-zinc-500 text-xs">Acceso completo a todo el contenido por $99/año</p>
                
                {/* Opción de solo crear cuenta */}
                <Link to="/register" onClick={closePopup}>
                  <Button variant="ghost" className="w-full text-zinc-400 hover:text-pink-400 mt-2 text-sm">
                    Solo quiero crear mi cuenta gratis →
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Landing;
